/**
 * "Jump to the newest message".
 *
 * Lives here rather than in a surface because the scroll container it watches belongs to
 * `ChannelMessageList` — a surface has no handle on it. In the reversed container the
 * bottom is `scrollTop === 0` and scrolling UP goes negative, which is why the threshold
 * is a negative number and the jump target is a large positive one.
 */

import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { useLayoutEffect } from "react";
import { EASE_IN_STRONG, EASE_OUT_STRONG } from "@/components/animations";
import { Button } from "@/components/ui/button/button";
import { BwChevronDownLine } from "@/components/ui/icons";
import { scrollChannelToBottom } from "../_libs/channel-scroll";

/** How far up the thread must be scrolled before the jump button appears. */
const VISIBLE_AFTER_SCROLL_PX = -200;

/**
 * Hidden state.
 *
 * `y` is a DELTA on the resting offset, not an absolute position: Tailwind v4 draws
 * `-translate-y-full` with the `translate` property while Motion writes `transform`, and
 * CSS applies `translate` first, so the two compose instead of fighting. `100%` therefore
 * cancels the lift and parks the button one own-height lower — flush over the composer's
 * top edge, the same one-height travel shadcn's `translate-y-full` makes.
 *
 * Nothing clips it (the layer it rides is zero-height with no overflow of its own), but
 * {@link EASE_IN_STRONG} back-loads the travel, so it only crosses the composer in the
 * last few frames, by which point it is most of the way faded.
 *
 * `y` stays a percentage string on BOTH ends — `"0%"`, never `0` — so Motion never has to
 * interpolate across a unit change.
 */
const HIDDEN = { opacity: 0, y: "100%", scale: 0.95 } as const;

/** Resting state: the position the className alone already describes. */
const SHOWN = { opacity: 1, y: "0%", scale: 1 } as const;

/**
 * Press feedback: the button dips a couple of pixels under the pointer.
 *
 * `translate` is named in the transition list because Tailwind v4 draws `translate-y-*`
 * with the `translate` property — a list naming `transform` would animate nothing. It
 * rides the inner button rather than the animated wrapper, so the dip and the
 * show/hide slide never contend for one property.
 *
 * `motion-reduce:transition-none` drops the easing and keeps the dip itself: a press
 * response is feedback, not decoration.
 */
const PRESS =
  "transition-[color,box-shadow,translate] active:translate-y-0.5 duration-100 motion-reduce:transition-none relative after:content-[''] after:absolute after:inset-0 after:bg-bg-surface-elevate-low after:rounded-full after:z-10";

export type ChannelScrollToBottomProps = {
  containerRef: React.RefObject<HTMLElement | null>;
  className?: string;
};

export function ChannelScrollToBottom({
  containerRef,
  className,
}: ChannelScrollToBottomProps) {
  const [isVisible, setIsVisible] = useToggle(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () =>
      setIsVisible(container.scrollTop < VISIBLE_AFTER_SCROLL_PX);

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    // Drops the slide and the scale but keeps the crossfade when the OS asks for less
    // motion. Outside `AnimatePresence` so the `motion.div` stays its direct child.
    <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className={cn(
              /* sorted */
              "absolute z-20 top-0 left-1/2 -translate-x-1/2",
              "rounded-full",
              "-translate-y-full",
              className,
            )}
            transition={{ duration: 0.2, ease: EASE_OUT_STRONG }}
            initial={HIDDEN}
            animate={SHOWN}
            exit={{
              ...HIDDEN,
              // `AnimatePresence` holds the node for the whole exit; without this the
              // button stays hoverable and clickable while it drops away. Motion cannot
              // tween `"none"`, so it lands on the first frame — which is what we want.
              pointerEvents: "none",
              transition: { duration: 0.4, ease: EASE_IN_STRONG },
            }}
          >
            <Button
              data-test="scroll-to-bottom-button"
              className={PRESS}
              kind="outline"
              variant="neutral"
              size="icon-md"
              onClick={() => scrollChannelToBottom(containerRef.current)}
            >
              <BwChevronDownLine size={20} className="z-20" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
}
